import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Health & Body — the physical foundation, kept deliberately calm.
 *
 * Guardrails from the product docs are load-bearing here:
 *  - wellness SIGNALS, not medical truth (no diagnoses, no medical claims)
 *  - no obsessive metric wall, no life score, no shame language
 *  - fast capture over manual logging; a single daily check-in is enough
 *
 * A HealthLog is one lightweight daily reading: sleep, energy, and movement.
 * "Readiness" is a neutral band (rest / steady / strong) that suggests how to
 * spend energy today — never a verdict on the person.
 */

export interface HealthLog extends RecordEnvelope {
  readonly kind: 'health_log';
  /** ISO date the reading is counted under. */
  readonly occurredOn: string;
  readonly sleepHours: number;
  /** Self-rated energy, 1 (depleted) .. 5 (excellent). */
  readonly energy: number;
  readonly movementMinutes: number;
  readonly note: string;
}

export interface HealthLogInput {
  readonly occurredOn?: string;
  readonly sleepHours?: number;
  readonly energy?: number;
  readonly movementMinutes?: number;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

export type ReadinessBand = 'rest' | 'steady' | 'strong';

/** Clamp energy to the 1..5 scale. */
export function clampEnergy(energy: number): number {
  if (Number.isNaN(energy)) return 3;
  return Math.min(5, Math.max(1, Math.round(energy)));
}

/**
 * A gentle readiness band from sleep + energy. This is guidance for how to
 * spend the day, not a health score. Low sleep or low energy suggests rest;
 * solid sleep and high energy suggests a strong day; everything else is steady.
 */
export function readinessBand(sleepHours: number, energy: number): ReadinessBand {
  const e = clampEnergy(energy);
  if (e <= 2 || sleepHours < 6) return 'rest';
  if (e >= 4 && sleepHours >= 7) return 'strong';
  return 'steady';
}

export interface HealthSummary {
  readonly daysLogged: number;
  readonly avgSleepHours: number;
  readonly avgEnergy: number;
  readonly totalMovementMinutes: number;
}

const dayOf = (iso: string): string => iso.slice(0, 10);

/** Neutral averages over [fromIso, toIso] (inclusive dates). Pure. */
export function summarizeHealth(
  logs: readonly Pick<HealthLog, 'occurredOn' | 'sleepHours' | 'energy' | 'movementMinutes'>[],
  fromIso: string,
  toIso: string,
): HealthSummary {
  const from = dayOf(fromIso);
  const to = dayOf(toIso);
  const inRange = logs.filter((l) => {
    const d = dayOf(l.occurredOn);
    return d >= from && d <= to;
  });
  const days = new Set(inRange.map((l) => dayOf(l.occurredOn)));
  const n = inRange.length;
  if (n === 0) {
    return { daysLogged: 0, avgSleepHours: 0, avgEnergy: 0, totalMovementMinutes: 0 };
  }
  const sumSleep = inRange.reduce((s, l) => s + l.sleepHours, 0);
  const sumEnergy = inRange.reduce((s, l) => s + clampEnergy(l.energy), 0);
  const sumMove = inRange.reduce((s, l) => s + Math.max(0, l.movementMinutes), 0);
  const round1 = (x: number): number => Math.round(x * 10) / 10;
  return {
    daysLogged: days.size,
    avgSleepHours: round1(sumSleep / n),
    avgEnergy: round1(sumEnergy / n),
    totalMovementMinutes: sumMove,
  };
}
